import { create } from 'zustand';

// Default pin types — all visible. 'event' is a synthetic type: not a pin in
// the DB, but rendered on the map as markers for events with lat/lng so users
// can filter them alongside the real pin types.
const DEFAULT_PIN_TYPES: Record<string, boolean> = {
  gallery: true, museum: true, art_centre: true, studio: true, venue: true,
  artist: true, heritage: true, supplier: true, education: true, residency: true,
  public_art: true, cultural_site: true, collection: true, event: true, artwork: true,
};

interface FilterState {
  // WHAT
  routeType: string;
  profileType: string;
  articleCategory: string;
  eventType: string;
  discoverType: string;
  pinTypes: Record<string, boolean>;

  // MAP
  showRadiusRing: boolean;

  // WHERE (country in searchStore; these are filter-specific)
  // country is in searchStore already

  // WHEN
  eventSort: string;
  dateFrom: string;  // ISO date string, e.g. '2025-04-01'
  dateTo: string;    // ISO date string
  eventTimeFilter: 'all' | 'now' | 'week' | 'month' | 'later';

  // Actions — MAP
  setShowRadiusRing: (v: boolean) => void;

  // Actions — WHAT
  setRouteType: (v: string) => void;
  setProfileType: (v: string) => void;
  setArticleCategory: (v: string) => void;
  setEventType: (v: string) => void;
  setDiscoverType: (v: string) => void;
  setPinType: (type: string, visible: boolean) => void;
  setPinTypesAll: (visible: boolean) => void;

  // Actions — WHEN
  setEventSort: (v: string) => void;
  setDateFrom: (v: string) => void;
  setDateTo: (v: string) => void;
  setEventTimeFilter: (v: 'all' | 'now' | 'week' | 'month' | 'later') => void;

  // Global
  clearAllFilters: () => void;
}

const STORAGE_KEY = 'artrouting-filters';

type FilterData = Omit<FilterState, 'setRouteType' | 'setProfileType' | 'setArticleCategory' | 'setEventType' | 'setEventSort' | 'setDiscoverType' | 'setPinType' | 'setPinTypesAll' | 'setShowRadiusRing' | 'clearAllFilters' | 'setDateFrom' | 'setDateTo' | 'setEventTimeFilter'>;

function loadFromStorage(): FilterData {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const p = JSON.parse(raw);
      return {
        routeType: typeof p.routeType === 'string' ? p.routeType : '',
        profileType: typeof p.profileType === 'string' ? p.profileType : '',
        articleCategory: typeof p.articleCategory === 'string' ? p.articleCategory : '',
        eventType: typeof p.eventType === 'string' ? p.eventType : '',
        eventSort: typeof p.eventSort === 'string' ? p.eventSort : 'start_date_desc',
        discoverType: typeof p.discoverType === 'string' ? p.discoverType : '',
        pinTypes: (p.pinTypes && typeof p.pinTypes === 'object') ? { ...DEFAULT_PIN_TYPES, ...p.pinTypes } : { ...DEFAULT_PIN_TYPES },
        showRadiusRing: typeof p.showRadiusRing === 'boolean' ? p.showRadiusRing : true,
        dateFrom: typeof p.dateFrom === 'string' ? p.dateFrom : '',
        dateTo: typeof p.dateTo === 'string' ? p.dateTo : '',
        eventTimeFilter: (['all', 'now', 'week', 'month', 'later'] as const).includes(p.eventTimeFilter) ? p.eventTimeFilter : 'all',
      };
    }
  } catch { /* ignore */ }
  return {
    routeType: '', profileType: '', articleCategory: '',
    eventType: '', eventSort: 'start_date_asc', discoverType: '',
    pinTypes: { ...DEFAULT_PIN_TYPES }, showRadiusRing: true,
    dateFrom: '', dateTo: '', eventTimeFilter: 'all' as const,
  };
}

function persist(state: Record<string, unknown>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch { /* ignore */ }
}

function snapshot(get: () => FilterState): FilterData {
  const s = get();
  return {
    routeType: s.routeType, profileType: s.profileType, articleCategory: s.articleCategory,
    eventType: s.eventType, eventSort: s.eventSort, discoverType: s.discoverType,
    pinTypes: s.pinTypes, showRadiusRing: s.showRadiusRing, dateFrom: s.dateFrom, dateTo: s.dateTo,
    eventTimeFilter: s.eventTimeFilter,
  };
}

const initial = loadFromStorage();

export const useFilterStore = create<FilterState>((set, get) => ({
  ...initial,

  // MAP
  setShowRadiusRing: (v) => { const n = { ...snapshot(get), showRadiusRing: v }; persist(n); set({ showRadiusRing: v }); },

  // WHAT
  setRouteType: (v) => { const n = { ...snapshot(get), routeType: v }; persist(n); set({ routeType: v }); },
  setProfileType: (v) => { const n = { ...snapshot(get), profileType: v }; persist(n); set({ profileType: v }); },
  setArticleCategory: (v) => { const n = { ...snapshot(get), articleCategory: v }; persist(n); set({ articleCategory: v }); },
  setEventType: (v) => { const n = { ...snapshot(get), eventType: v }; persist(n); set({ eventType: v }); },
  setDiscoverType: (v) => { const n = { ...snapshot(get), discoverType: v }; persist(n); set({ discoverType: v }); },
  setPinType: (type, visible) => {
    const s = get();
    const pinTypes = { ...s.pinTypes, [type]: visible };
    const n = { ...snapshot(get), pinTypes };
    persist(n);
    set({ pinTypes });
  },
  setPinTypesAll: (visible) => {
    const pinTypes: Record<string, boolean> = {};
    for (const key of Object.keys(DEFAULT_PIN_TYPES)) pinTypes[key] = visible;
    const n = { ...snapshot(get), pinTypes };
    persist(n);
    set({ pinTypes });
  },

  // WHEN
  setEventSort: (v) => { const n = { ...snapshot(get), eventSort: v }; persist(n); set({ eventSort: v }); },
  setDateFrom: (v) => { const n = { ...snapshot(get), dateFrom: v }; persist(n); set({ dateFrom: v }); },
  setDateTo: (v) => { const n = { ...snapshot(get), dateTo: v }; persist(n); set({ dateTo: v }); },
  setEventTimeFilter: (v) => { const n = { ...snapshot(get), eventTimeFilter: v }; persist(n); set({ eventTimeFilter: v }); },

  clearAllFilters: () => {
    const fresh: FilterData = {
      routeType: '', profileType: '', articleCategory: '',
      eventType: '', eventSort: 'start_date_asc', discoverType: '',
      pinTypes: { ...DEFAULT_PIN_TYPES }, showRadiusRing: true,
      dateFrom: '', dateTo: '', eventTimeFilter: 'all',
    };
    persist(fresh);
    set(fresh);
  },
}));

/** Count of active (non-default) filters for badge display */
export function countActiveFilters(state: FilterState): number {
  let count = 0;
  if (state.routeType) count++;
  if (state.profileType) count++;
  if (state.articleCategory) count++;
  if (state.eventType) count++;
  if (state.discoverType) count++;
  if (state.dateFrom) count++;
  if (state.dateTo) count++;
  for (const v of Object.values(state.pinTypes)) {
    if (!v) count++;
  }
  return count;
}
